import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, Download, Package, Search } from "lucide-react";
import { endpoints } from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TBody, TD, TH, THead, TR } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { CenteredLoader, ErrorState, EmptyState } from "@/components/ui/feedback";
import { useOperatoreContext } from "@/lib/operatoreContext";
import { isHazardousEER } from "@/lib/utils";

const GIACENZA_ALERT_KG = 10_000;

export function Giacenze() {
  const ctx = useOperatoreContext();
  const [registroId, setRegistroId] = useState(ctx.registroId);
  const [active, setActive] = useState<string | null>(ctx.registroId || null);
  const [anno, setAnno] = useState<string>(String(new Date().getFullYear()));

  useEffect(() => {
    if (ctx.registroId) {
      setRegistroId(ctx.registroId);
      setActive(ctx.registroId);
    }
  }, [ctx.registroId]);

  const q = useQuery({
    queryKey: ["giacenze", active],
    queryFn: () => endpoints.giacenze.perRegistro(active!),
    enabled: !!active,
  });

  const mudDownload = useMutation({
    mutationFn: () => endpoints.mud.downloadCsv(active!, parseInt(anno, 10)),
    onSuccess: () => toast.success("Download MUD avviato"),
    onError: (e: Error) => toast.error(`Download MUD fallito: ${e.message}`),
  });

  const alertRows =
    q.data?.rows.filter((r) => r.giacenza_kg >= GIACENZA_ALERT_KG || r.giacenza_kg < 0) ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Giacenze rifiuti</h1>
        <p className="text-sm text-muted-foreground">
          Calcolate sui movimenti del registro selezionato in sidebar
        </p>
      </div>

      {!ctx.registroId && (
        <Card className="border-amber-200 bg-amber-50/50 dark:border-amber-900 dark:bg-amber-950/20">
          <CardContent className="flex flex-wrap items-center gap-2 p-4 text-sm">
            <Package className="h-4 w-4 text-amber-600" />
            <span>
              Seleziona un registro in{" "}
              <Link to="/registri" className="underline font-medium">
                Registro
              </Link>{" "}
              o inserisci l&apos;ID manualmente.
            </span>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Registro</CardTitle>
          <p className="text-xs text-muted-foreground">
            {ctx.registroId
              ? "Sincronizzato con il contesto in sidebar"
              : "ID registro RENTRI (UUID o identificativo)"}
          </p>
        </CardHeader>
        <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1">
            <label className="text-xs font-medium text-muted-foreground">ID registro</label>
            <Input
              className="mt-1 font-mono text-sm"
              placeholder="UUID registro"
              value={registroId}
              onChange={(e) => setRegistroId(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") setActive(registroId.trim() || null);
              }}
            />
          </div>
          <Button onClick={() => setActive(registroId.trim() || null)} disabled={!registroId.trim()}>
            <Search className="mr-2 h-4 w-4" /> Calcola
          </Button>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Anno MUD</label>
            <Input
              type="number"
              min={2020}
              max={2100}
              value={anno}
              onChange={(e) => setAnno(e.target.value)}
              className="mt-1 w-24"
            />
          </div>
          <Button
            variant="outline"
            disabled={!active || mudDownload.isPending}
            onClick={() => active && mudDownload.mutate()}
          >
            <Download className="mr-2 h-4 w-4" /> MUD CSV
          </Button>
        </CardContent>
      </Card>

      {active && alertRows.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/50 dark:border-amber-900 dark:bg-amber-950/20">
          <CardContent className="flex flex-wrap items-start gap-2 p-4 text-sm">
            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium">Attenzione giacenze</p>
              <ul className="mt-1 list-disc pl-4 text-muted-foreground">
                {alertRows.map((r) => (
                  <li key={r.eer}>
                    EER {r.eer}:{" "}
                    {r.giacenza_kg < 0
                      ? "giacenza negativa"
                      : `giacenza elevata (${r.giacenza_kg.toLocaleString("it-IT")} kg)`}
                  </li>
                ))}
              </ul>
            </div>
          </CardContent>
        </Card>
      )}

      {active && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Aggregato per EER</CardTitle>
              {q.data && (
                <div className="flex gap-2 text-xs">
                  <Badge variant="outline">{q.data.totale_movimenti} movimenti</Badge>
                  <Badge>
                    Giacenza tot. {q.data.totale_giacenza_kg.toLocaleString("it-IT", { maximumFractionDigits: 1 })} kg
                  </Badge>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {q.isLoading && <CenteredLoader />}
            {q.error && <ErrorState error={q.error} />}
            {q.data && q.data.rows.length === 0 && (
              <EmptyState
                title="Nessun movimento"
                description="Verifica l'ID registro o registra movimenti in Registro"
              />
            )}
            {q.data && q.data.rows.length > 0 && (
              <Table>
                <THead>
                  <TR>
                    <TH>EER</TH>
                    <TH className="text-right">Carichi (kg)</TH>
                    <TH className="text-right">Scarichi (kg)</TH>
                    <TH className="text-right">Giacenza (kg)</TH>
                    <TH className="text-right"># C / # S</TH>
                  </TR>
                </THead>
                <TBody>
                  {q.data.rows.map((r) => (
                    <TR key={r.eer}>
                      <TD className="font-mono text-xs">
                        <span className="flex items-center gap-2">
                          {r.eer}
                          {isHazardousEER(r.eer) && (
                            <Badge variant="destructive" className="text-[10px]">
                              P
                            </Badge>
                          )}
                        </span>
                      </TD>
                      <TD className="text-right tabular-nums">
                        {r.carico_kg.toLocaleString("it-IT", { maximumFractionDigits: 1 })}
                      </TD>
                      <TD className="text-right tabular-nums">
                        {r.scarico_kg.toLocaleString("it-IT", { maximumFractionDigits: 1 })}
                      </TD>
                      <TD className="text-right tabular-nums font-semibold">
                        <span
                          className={
                            r.giacenza_kg < 0
                              ? "text-rose-600"
                              : r.giacenza_kg >= GIACENZA_ALERT_KG
                                ? "text-amber-700 dark:text-amber-400"
                                : ""
                          }
                        >
                          {r.giacenza_kg.toLocaleString("it-IT", { maximumFractionDigits: 1 })}
                        </span>
                      </TD>
                      <TD className="text-right text-muted-foreground">
                        {r.n_carichi} / {r.n_scarichi}
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
